/**
 * Northstar category emblems — Stage 2.
 *
 * Ten hand-authored SVGs sharing one visual grammar:
 *   - viewBox 24 × 24, stroke-based
 *   - stroke-width 1.75, round caps + joins
 *   - single mint-green stroke (#B8EFC6), no fills except tiny accent dots
 *   - Optical weight tuned so no icon feels heavier than another
 *   - Rendered inside the flat luminous badge (see OrbCanvas Category)
 *
 * Each icon accepts { size, className } and is decorative when rendered
 * inside an already-labeled <button>. Parent supplies aria-label.
 *
 *   <button aria-label="Legal. Know your rights..."><LegalEmblem/></button>
 *
 * Anti-orb rule: no fills, no gradients, no 3D. The icon is what the eye
 * reads. The badge stays flat.
 */
import React from "react";

const STROKE = "#B8EFC6";

function Frame({ size = 44, className = "", children }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      role="img"
      aria-hidden="true"
      className={className}
      fill="none"
      stroke={STROKE}
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      data-emblem-family="northstar-v1"
    >
      {children}
    </svg>
  );
}

/* 1. Legal — two-pan balance scales, symmetric fulcrum */
export const LegalEmblem = (p) => (
  <Frame {...p}>
    <line x1="12" y1="4" x2="12" y2="20" />
    <line x1="7" y1="20" x2="17" y2="20" />
    <line x1="5" y1="7" x2="19" y2="7" />
    {/* left pan */}
    <path d="M3 7 L7 15 L3 15 Z" />
    <path d="M3 15 Q3 16 4 16 L6 16 Q7 16 7 15" />
    {/* right pan */}
    <path d="M17 15 L21 7 L21 15 Z" />
    <path d="M17 15 Q17 16 18 16 L20 16 Q21 16 21 15" />
  </Frame>
);

/* 2. Housing — house outline, simple pitched roof */
export const HousingEmblem = (p) => (
  <Frame {...p}>
    <path d="M4 11 L12 4 L20 11" />
    <path d="M6 10 L6 20 L18 20 L18 10" />
    <path d="M10 20 L10 14 L14 14 L14 20" />
  </Frame>
);

/* 3. Immigration — globe with latitude arcs + meridian */
export const ImmigrationEmblem = (p) => (
  <Frame {...p}>
    <circle cx="12" cy="12" r="8" />
    <ellipse cx="12" cy="12" rx="8" ry="3.2" />
    <path d="M12 4 Q7 12 12 20" />
    <path d="M12 4 Q17 12 12 20" />
  </Frame>
);

/* 4. Retail — shopping cart */
export const RetailEmblem = (p) => (
  <Frame {...p}>
    <path d="M3 5 L5.5 5 L8 16 L18 16" />
    <path d="M6.5 8 L20 8 L18.5 14 L7.5 14" />
    <circle cx="9" cy="19" r="1.2" fill={STROKE} />
    <circle cx="17" cy="19" r="1.2" fill={STROKE} />
  </Frame>
);

/* 5. Nourishment — crossed fork + spoon (NOT a plate, NOT a restaurant) */
export const NourishmentEmblem = (p) => (
  <Frame {...p}>
    {/* fork on the left */}
    <path d="M7 3 L7 10" />
    <path d="M7 10 Q7 13 6 13 L6 21" />
    <path d="M5 3 L5 7" />
    <path d="M9 3 L9 7" />
    <path d="M5 7 Q7 8 9 7" />
    {/* spoon on the right */}
    <ellipse cx="17" cy="6" rx="2.6" ry="3.4" />
    <path d="M17 9.5 L17 21" />
  </Frame>
);

/* 6. Wellness & Wellbeing — six-petal lotus */
export const WellnessEmblem = (p) => (
  <Frame {...p}>
    {/* center bud */}
    <path d="M12 8 Q10 14 12 18 Q14 14 12 8" />
    {/* left petal */}
    <path d="M6 12 Q6 8 12 14" />
    {/* right petal */}
    <path d="M18 12 Q18 8 12 14" />
    {/* outer left */}
    <path d="M4 15 Q4 11 10 16" />
    {/* outer right */}
    <path d="M20 15 Q20 11 14 16" />
    {/* baseline */}
    <path d="M6 18 Q12 20 18 18" />
  </Frame>
);

/* 7. Personal Trainers — horizontal dumbbell */
export const TrainersEmblem = (p) => (
  <Frame {...p}>
    <line x1="8" y1="12" x2="16" y2="12" />
    <rect x="4" y="9" width="3" height="6" rx="0.6" />
    <rect x="17" y="9" width="3" height="6" rx="0.6" />
    <line x1="3" y1="10.5" x2="3" y2="13.5" />
    <line x1="21" y1="10.5" x2="21" y2="13.5" />
  </Frame>
);

/* 8. Coaches — person reaching upward toward a star */
export const CoachesEmblem = (p) => (
  <Frame {...p}>
    {/* head */}
    <circle cx="10" cy="7" r="2" />
    {/* torso + reaching arm */}
    <path d="M10 9 L10 15" />
    <path d="M10 11 L15 8" />
    {/* legs */}
    <path d="M10 15 L7 20" />
    <path d="M10 15 L13 20" />
    {/* star */}
    <path d="M18 5 L18.7 6.6 L20.4 6.7 L19.1 7.9 L19.5 9.6 L18 8.7 L16.5 9.6 L16.9 7.9 L15.6 6.7 L17.3 6.6 Z" />
  </Frame>
);

/* 9. Therapists — two speech shapes forming a heart (dialogue + support) */
export const TherapistsEmblem = (p) => (
  <Frame {...p}>
    {/* left speech, curving right into heart lobe */}
    <path d="M4 9 Q4 5 8 5 L11 5 Q13 5 13 7 L13 10 L10 13 Q4 12 4 9 Z" />
    {/* right speech mirrored */}
    <path d="M20 12 Q20 8 16 8 L13 8 Q11 8 11 10 L11 13 L14 16 Q20 15 20 12 Z" />
  </Frame>
);

/* 10. Aesthetics — face profile with a soft leaf accent at the temple */
export const AestheticsEmblem = (p) => (
  <Frame {...p}>
    {/* face profile */}
    <path d="M14 4 Q9 4 9 10 Q9 12 10 13 L10 16 Q10 17 11 17 L11 19 Q11 20 12 20 L15 20" />
    {/* nose */}
    <path d="M9 11 Q7 12 9 13" />
    {/* eye dot */}
    <circle cx="11.5" cy="10" r="0.4" fill={STROKE} />
    {/* soft leaf at temple */}
    <path d="M15 5 Q19 5 18 8 Q16 8 15 5 Z" />
    <line x1="15" y1="5" x2="17" y2="7" />
  </Frame>
);

/** Map orb key → emblem component. Single source of truth. */
export const EMBLEMS = {
  legal: LegalEmblem,
  housing: HousingEmblem,
  immigration: ImmigrationEmblem,
  retail: RetailEmblem,
  nourishment: NourishmentEmblem,
  wellness: WellnessEmblem,
  trainers: TrainersEmblem,
  coaches: CoachesEmblem,
  therapists: TherapistsEmblem,
  aesthetics: AestheticsEmblem,
};

/** Component that renders the right emblem for a given orb key. */
export default function CategoryEmblem({ orbKey, size = 44 }) {
  const Comp = EMBLEMS[orbKey];
  if (!Comp) return null;
  return <Comp size={size} />;
}
